import { type ReactNode } from 'react';

import { Box, type BoxProps, Stack, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';

export const PencilEditIcon = ({
  size = 16,
  sx,
}: {
  size?: number;
  sx?: BoxProps['sx'];
}) => (
  <Box
    className="edit-icon"
    sx={[
      {
        color: 'text.secondary',
        opacity: 0.5,
        transition: 'opacity 0.15s ease',
        display: 'flex',
        alignItems: 'center',
      },
      ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
    ]}
  >
    <Pencil size={size} />
  </Box>
);

type EditableSectionHeaderProps = {
  title: string;
  onEdit?: () => void;
  children?: ReactNode;
  /**
   * Render `children` outside the click-to-edit button. Required whenever
   * children contain interactive elements (e.g. a collapse toggle) so we don't
   * nest interactive elements inside the header `<button>`.
   */
  interactiveChildren?: boolean;
};

const EditableSectionHeader = ({
  title,
  onEdit,
  children,
  interactiveChildren = false,
}: EditableSectionHeaderProps) => {
  const content = children && (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>{children}</Box>
  );

  return (
    <>
      <Box
        {...(onEdit && {
          component: 'button',
          type: 'button',
          onClick: onEdit,
          'aria-label': `Edit ${title}`,
        })}
        sx={{
          position: 'relative',
          width: '100%',
          ...(onEdit && {
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            p: 0,
            textAlign: 'left',
            outline: 'none',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
            '&:hover .edit-icon, &:focus-visible .edit-icon': {
              opacity: 1,
            },
          }),
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>

          {onEdit && <PencilEditIcon />}
        </Stack>

        {!interactiveChildren && content}
      </Box>

      {interactiveChildren && content}
    </>
  );
};

export default EditableSectionHeader;
